<template>
  <div class="admin-analytics">
    <v-container class="py-6">
      <!-- Header -->
      <v-row class="mb-6">
        <v-col cols="12">
          <h1 class="text-h3 text-gradient mb-2">{{ $t('adminAnalytics.title') }}</h1>
          <p class="text-h6 text-medium-emphasis">{{ $t('adminAnalytics.subtitle') }}</p>
        </v-col>
      </v-row>

      <!-- Stats Cards -->
      <v-row class="mb-6">
        <v-col cols="12" sm="4">
          <v-card color="primary" variant="tonal" elevation="4">
            <v-card-text class="text-center pa-6">
              <v-icon icon="mdi-account-multiple" size="48" class="mb-2" />
              <div class="text-h3 font-weight-bold">{{ data.totalStudents }}</div>
              <div class="text-body-1">{{ $t('adminAnalytics.totalStudents') }}</div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="4">
          <v-card color="success" variant="tonal" elevation="4">
            <v-card-text class="text-center pa-6">
              <v-icon icon="mdi-account-check" size="48" class="mb-2" />
              <div class="text-h3 font-weight-bold">{{ data.activeStudents }}</div>
              <div class="text-body-1">{{ $t('adminAnalytics.activeStudents') }}</div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="4">
          <v-card color="warning" variant="tonal" elevation="4">
            <v-card-text class="text-center pa-6">
              <v-icon icon="mdi-account-off" size="48" class="mb-2" />
              <div class="text-h3 font-weight-bold">{{ data.inactiveStudents }}</div>
              <div class="text-body-1">{{ $t('adminAnalytics.inactiveStudents') }}</div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Filter + Search -->
      <v-row class="mb-4">
        <v-col cols="12" sm="6">
          <v-text-field
            v-model="search"
            :label="$t('adminAnalytics.search')"
            prepend-inner-icon="mdi-magnify"
            variant="outlined"
            density="compact"
            clearable
            hide-details
          />
        </v-col>
        <v-col cols="12" sm="6">
          <v-btn-toggle v-model="filter" mandatory density="compact">
            <v-btn value="all">{{ $t('adminAnalytics.all') }}</v-btn>
            <v-btn value="weak">{{ $t('adminAnalytics.withWeakTopics') }}</v-btn>
            <v-btn value="inactive">{{ $t('adminAnalytics.inactive') }}</v-btn>
          </v-btn-toggle>
        </v-col>
      </v-row>

      <!-- Students Table -->
      <v-card elevation="4">
        <v-card-title class="d-flex align-center">
          <v-icon class="me-2">mdi-chart-bar</v-icon>
          {{ $t('adminAnalytics.ranking') }}
          <v-spacer />
          <v-btn
            color="primary"
            variant="outlined"
            size="small"
            prepend-icon="mdi-refresh"
            :loading="loading"
            @click="loadData"
          >
            {{ $t('common.refresh') }}
          </v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="pa-0">
          <v-data-table
            :headers="headers"
            :items="filteredStudents"
            :loading="loading"
            :search="search"
            :items-per-page="20"
            density="comfortable"
            hover
          >
            <template #item.rank="{ item }">
              <v-icon v-if="item.rank === 1" color="amber" size="20">mdi-crown</v-icon>
              <v-icon v-else-if="item.rank === 2" color="grey" size="20">mdi-medal</v-icon>
              <v-icon v-else-if="item.rank === 3" color="brown" size="20">mdi-medal</v-icon>
              <span v-else>{{ item.rank || '-' }}</span>
            </template>

            <template #item.avgScore="{ item }">
              <v-chip
                size="small"
                :color="item.avgScore >= 80 ? 'success' : item.avgScore >= 60 ? 'warning' : item.avgScore > 0 ? 'error' : 'grey'"
                variant="tonal"
              >
                {{ item.avgScore }}%
              </v-chip>
            </template>

            <template #item.answered="{ item }">
              <div class="d-flex flex-column ga-1" style="min-width: 210px;">
                <div class="d-flex align-center ga-2">
                  <v-progress-linear
                    :model-value="item.progress"
                    color="primary"
                    height="6"
                    rounded
                    style="min-width: 60px; max-width: 90px;"
                  />
                  <span class="text-caption">{{ item.answered }}/{{ item.totalTests }}</span>
                </div>
                <span class="text-caption text-medium-emphasis">
                  {{ roundLabel(item.currentRoundNum) }}
                </span>
                <v-tooltip
                  :text="$t('adminUsers.uniqueCoverageHint') || 'Бірегей тесттер саны (қайталанулар есепке алынбайды)'"
                  location="top"
                >
                  <template #activator="{ props }">
                    <div v-bind="props" class="d-flex align-center ga-2">
                      <v-progress-linear
                        :model-value="item.uniqueProgress || 0"
                        color="success"
                        height="6"
                        rounded
                        style="min-width: 60px; max-width: 90px;"
                      />
                      <span class="text-caption">
                        <v-icon size="x-small" color="success" class="mr-1">mdi-check-decagram</v-icon>
                        {{ item.uniqueAnswered || 0 }}/{{ item.totalTests }}
                      </span>
                    </div>
                  </template>
                </v-tooltip>
              </div>
            </template>

            <template #item.weakTopics="{ item }">
              <div v-if="item.weakTopics && item.weakTopics.length > 0" class="d-flex flex-wrap ga-1">
                <v-chip
                  v-for="topic in item.weakTopics.slice(0, 3)"
                  :key="topic.category"
                  size="x-small"
                  color="error"
                  variant="tonal"
                >
                  {{ formatCategoryName(topic.category) }} ({{ topic.errorRate }}% {{ $t('adminAnalytics.errorRate') }})
                </v-chip>
                <v-chip v-if="item.weakTopics.length > 3" size="x-small" variant="text">
                  +{{ item.weakTopics.length - 3 }}
                </v-chip>
              </div>
              <span v-else class="text-medium-emphasis">-</span>
            </template>

            <template #item.lastActivity="{ item }">
              <span v-if="item.lastActivity">{{ formatDate(item.lastActivity) }}</span>
              <span v-else class="text-medium-emphasis">{{ $t('adminAnalytics.never') }}</span>
            </template>

            <template #item.actions="{ item }">
              <v-btn
                size="small"
                prepend-icon="mdi-eye"
                variant="tonal"
                color="primary"
                :loading="detailsLoading && selectedStudentId === item.id"
                @click="openStudentDetails(item)"
              >
                {{ $t('view') }}
              </v-btn>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-container>

    <v-snackbar v-model="showError" color="error">{{ errorMessage }}</v-snackbar>
    <v-snackbar v-model="showSuccess" color="success">{{ successMessage }}</v-snackbar>

    <v-dialog v-model="detailsDialog" max-width="1200" scrollable>
      <v-card>
        <v-card-title class="d-flex align-center">
          {{ $t('details') }}: {{ studentDetails?.student?.fullName || '-' }}
          <v-spacer />
          <v-btn icon="mdi-close" variant="text" @click="detailsDialog = false" />
        </v-card-title>
        <v-divider />

        <v-card-text class="pa-4">
          <div v-if="detailsLoading" class="text-center py-10">
            <v-progress-circular indeterminate color="primary" size="48" />
          </div>

          <div v-else-if="studentDetails">
            <v-row class="mb-4">
              <v-col cols="12" md="4">
                <v-card variant="tonal" color="primary">
                  <v-card-text>
                    <div class="text-caption text-medium-emphasis">{{ $t('adminAnalytics.student') }}</div>
                    <div class="text-subtitle-1 font-weight-bold">{{ studentDetails.student.fullName }}</div>
                    <div class="text-body-2">{{ studentDetails.student.phone }}</div>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12" md="4">
                <v-card variant="tonal" color="success">
                  <v-card-text>
                    <div class="text-caption text-medium-emphasis">{{ $t('adminAnalytics.tests') }}</div>
                    <div class="text-subtitle-1 font-weight-bold">{{ studentDetails.sessions.length }}</div>
                    <div class="text-body-2">{{ $t('tests.answered') }}: {{ totalDetailedAnswers }}</div>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12" md="4">
                <v-card variant="tonal" color="warning">
                  <v-card-text>
                    <div class="text-caption text-medium-emphasis">{{ $t('adminAnalytics.lastActivity') }}</div>
                    <div class="text-subtitle-1 font-weight-bold">
                      {{ studentDetails.sessions[0] ? formatDateTime(studentDetails.sessions[0].createdAt) : '-' }}
                    </div>
                    <div class="text-body-2">{{ $t('adminAnalytics.avgScore') }}: {{ studentAverageScore }}%</div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>

            <v-card variant="tonal" color="info" class="mb-4">
              <v-card-text>
                <div class="d-flex align-center justify-space-between flex-wrap ga-3 mb-3">
                  <div>
                    <div class="text-caption text-medium-emphasis">{{ $t('adminAnalytics.assignedTests') }}</div>
                    <div class="text-subtitle-1 font-weight-bold">
                      {{ activeAssignments.length ? activeAssignments.length : 0 }}
                    </div>
                  </div>
                  <div class="text-body-2 text-medium-emphasis">
                    {{ $t('adminAnalytics.assignedTestsHint') }}
                  </div>
                </div>

                <div v-if="activeAssignments.length" class="d-flex flex-column ga-2">
                  <v-card
                    v-for="assignment in activeAssignments"
                    :key="assignment.id"
                    variant="outlined"
                  >
                    <v-card-text class="pa-3 d-flex align-center justify-space-between flex-wrap ga-3">
                      <div class="d-flex flex-column ga-1">
                        <div class="font-weight-bold">{{ assignment.title }}</div>
                        <div class="text-body-2 text-medium-emphasis">
                          {{ $t('test.questionCount') }}: {{ assignment.questionCount }} • {{ formatDateTime(assignment.createdAt) }}
                        </div>
                        <div v-if="assignment.notes" class="text-body-2 text-medium-emphasis">
                          {{ assignment.notes }}
                        </div>
                      </div>

                      <div class="d-flex align-center flex-wrap ga-2">
                        <v-chip size="small" color="info" variant="tonal">
                          {{ assignmentStatusLabel(assignment.status) }}
                        </v-chip>
                        <v-btn
                          v-if="assignment.status === 'ACTIVE' || assignment.status === 'IN_PROGRESS'"
                          size="small"
                          variant="text"
                          color="error"
                          icon="mdi-close-circle-outline"
                          :loading="cancelAssignmentLoadingId === assignment.id"
                          @click="cancelStudentAssignment(assignment)"
                        />
                      </div>
                    </v-card-text>
                  </v-card>
                </div>

                <div v-else class="text-body-2 text-medium-emphasis">
                  {{ $t('adminAnalytics.noAssignedTests') }}
                </div>
              </v-card-text>
            </v-card>

            <v-expansion-panels multiple>
              <v-expansion-panel
                v-for="session in studentDetails.sessions"
                :key="session.id"
              >
                <v-expansion-panel-title>
                  <div class="d-flex flex-wrap align-center ga-2 w-100 pe-4">
                    <strong>#{{ session.id }}</strong>
                    <v-chip size="small" color="primary" variant="tonal">
                      {{ formatDateTime(session.createdAt) }}
                    </v-chip>
                    <v-chip size="small" variant="tonal">
                      {{ modeLabel(session.mode) }}
                    </v-chip>
                    <v-chip size="small" color="success" variant="tonal">
                      {{ $t('tests.score') }}: {{ session.score }}%
                    </v-chip>
                    <v-chip size="small" color="success" variant="tonal">
                      {{ $t('tests.correctAnswers') }}: {{ session.correctAnswers }}
                    </v-chip>
                    <v-chip size="small" color="error" variant="tonal">
                      {{ $t('tests.wrongAnswers') }}: {{ session.wrongAnswers }}
                    </v-chip>
                    <v-chip size="small" variant="tonal">
                      {{ $t('tests.answered') }}: {{ session.answeredCount }}/{{ session.totalQuestions }}
                    </v-chip>
                    <v-chip v-if="session.timeSpent" size="small" variant="tonal">
                      {{ $t('tests.timeSpent') }}: {{ formatDuration(session.timeSpent) }}
                    </v-chip>
                    <v-btn
                      size="small"
                      color="info"
                      variant="tonal"
                      prepend-icon="mdi-clipboard-text-play-outline"
                      @click.stop="openAssignDialog(session)"
                    >
                      {{ $t('adminAnalytics.assignTest') }}
                    </v-btn>
                  </div>
                </v-expansion-panel-title>

                <v-expansion-panel-text>
                  <div v-if="!session.answers.length" class="text-medium-emphasis">
                    {{ $t('adminAnalytics.noData') }}
                  </div>

                  <div v-else class="d-flex flex-column ga-3">
                    <v-card
                      v-for="answer in session.answers"
                      :key="`${session.id}-${answer.order}-${answer.testId}`"
                      variant="outlined"
                    >
                      <v-card-text class="pa-4">
                        <div class="d-flex flex-wrap align-center justify-space-between ga-2 mb-3">
                          <div class="text-subtitle-1 font-weight-bold">
                            {{ answer.order }}. {{ getLocalizedText(answer, 'question') || '-' }}
                          </div>
                          <v-chip
                            size="small"
                            :color="answer.isCorrect ? 'success' : 'error'"
                            variant="tonal"
                          >
                            {{ answer.isCorrect ? $t('adminUsers.rounds.correct') : $t('adminUsers.rounds.incorrect') }}
                          </v-chip>
                        </div>

                        <div class="text-body-2 mb-2">
                          <strong>{{ $t('tests.yourAnswer') }}:</strong>
                          {{ getLocalizedText(answer, 'selectedAnswer') || '-' }}
                        </div>

                        <div class="text-body-2 mb-2">
                          <strong>{{ $t('tests.correctAnswer') }}:</strong>
                          {{ getLocalizedText(answer, 'correctAnswer') || '-' }}
                        </div>

                        <div v-if="answer.topic || answer.category" class="text-body-2 mb-2">
                          <strong>{{ $t('adminAnalytics.weakTopicsList') }}:</strong>
                          {{ answer.topic || formatCategoryName(answer.category) }}
                        </div>

                        <div v-if="getLocalizedText(answer, 'explanation')" class="text-body-2">
                          <strong>{{ $t('details') }}:</strong>
                          {{ getLocalizedText(answer, 'explanation') }}
                        </div>
                      </v-card-text>
                    </v-card>
                  </div>
                </v-expansion-panel-text>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>
        </v-card-text>

        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="detailsDialog = false">{{ $t('common.close') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="assignDialog" max-width="900" scrollable>
      <v-card>
        <v-card-title class="d-flex align-center">
          {{ $t('adminAnalytics.assignTest') }}
          <v-spacer />
          <v-btn icon="mdi-close" variant="text" @click="assignDialog = false" />
        </v-card-title>
        <v-divider />
        <v-card-text class="pa-4">
          <div v-if="assignSession">
            <v-text-field
              v-model="assignTitle"
              :label="$t('adminAnalytics.assignmentTitle')"
              variant="outlined"
              class="mb-3"
            />

            <v-textarea
              v-model="assignNotes"
              :label="$t('adminAnalytics.assignmentNotes')"
              variant="outlined"
              rows="2"
              class="mb-3"
            />

            <div class="d-flex align-center justify-space-between flex-wrap ga-2 mb-3">
              <div class="text-body-2 text-medium-emphasis">
                {{ $t('adminAnalytics.assignmentQuestionsSelected', { count: assignSelectedTestIds.length, total: assignableAnswers.length }) }}
              </div>
              <div class="d-flex align-center ga-2">
                <v-btn size="small" variant="text" @click="selectAllAssignableQuestions">
                  {{ $t('adminAnalytics.selectAll') }}
                </v-btn>
                <v-btn size="small" variant="text" @click="clearAllAssignableQuestions">
                  {{ $t('adminAnalytics.clearSelection') }}
                </v-btn>
              </div>
            </div>

            <div class="d-flex flex-column ga-3">
              <v-card
                v-for="answer in assignableAnswers"
                :key="`assign-${assignSession.id}-${answer.testId}`"
                variant="outlined"
              >
                <v-card-text class="pa-3 d-flex align-start ga-3">
                  <v-checkbox-btn
                    v-model="assignSelectedTestIds"
                    :value="answer.testId"
                    color="info"
                  />
                  <div class="flex-grow-1">
                    <div class="font-weight-bold mb-1">
                      {{ answer.order }}. {{ getLocalizedText(answer, 'question') || '-' }}
                    </div>
                    <div class="text-body-2 text-medium-emphasis">
                      {{ answer.topic || formatCategoryName(answer.category) }}
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </div>
          </div>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="assignDialog = false">{{ $t('common.cancel') }}</v-btn>
          <v-btn
            color="info"
            variant="flat"
            :disabled="!assignSelectedTestIds.length"
            :loading="assignLoading"
            @click="submitAssignedTests"
          >
            {{ $t('adminAnalytics.assignTest') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import api from '@/utils/api'

const { t, locale } = useI18n()

const loading = ref(false)
const showError = ref(false)
const errorMessage = ref('')
const showSuccess = ref(false)
const successMessage = ref('')
const search = ref('')
const filter = ref('all')
const detailsDialog = ref(false)
const detailsLoading = ref(false)
const selectedStudentId = ref(null)
const studentDetails = ref(null)
const assignDialog = ref(false)
const assignLoading = ref(false)
const assignSession = ref(null)
const assignSelectedTestIds = ref([])
const assignTitle = ref('')
const assignNotes = ref('')
const cancelAssignmentLoadingId = ref(null)

const data = reactive({
  totalStudents: 0,
  activeStudents: 0,
  inactiveStudents: 0,
  ranking: [],
  inactive: []
})

const headers = computed(() => [
  { title: t('adminAnalytics.rank'), key: 'rank', width: '60px', sortable: false },
  { title: t('adminAnalytics.student'), key: 'fullName', sortable: true },
  { title: t('adminAnalytics.phone'), key: 'phone', sortable: false },
  { title: t('adminAnalytics.avgScore'), key: 'avgScore', sortable: true },
  { title: t('adminAnalytics.tests'), key: 'answered', sortable: true },
  { title: t('adminAnalytics.weakTopicsList'), key: 'weakTopics', sortable: false },
  { title: t('adminAnalytics.lastActivity'), key: 'lastActivity', sortable: true },
  { title: '', key: 'actions', sortable: false, width: '110px' }
])

const filteredStudents = computed(() => {
  if (filter.value === 'weak') {
    return data.ranking.filter(s => s.weakTopics && s.weakTopics.length > 0)
  }
  if (filter.value === 'inactive') {
    return data.inactive
  }
  return [...data.ranking, ...data.inactive]
})

const loadData = async () => {
  loading.value = true
  try {
    const response = await api.get('/admin/student-analytics')
    data.totalStudents = response.data.totalStudents || 0
    data.activeStudents = response.data.activeStudents || 0
    data.inactiveStudents = response.data.inactiveStudents || 0
    data.ranking = response.data.ranking || []
    data.inactive = response.data.inactive || []
  } catch (error) {
    errorMessage.value = error.response?.data?.error || t('adminAnalytics.loadError')
    showError.value = true
  } finally {
    loading.value = false
  }
}

const categoryNames = computed(() => ({
  general: t('student.progress.cat_general'),
  traffic_rules: t('student.progress.cat_traffic_rules'),
  road_signs: t('student.progress.cat_road_signs'),
  road_marking: t('student.progress.cat_road_marking'),
  traffic_signals: t('student.progress.cat_traffic_signals'),
  speed_limits: t('student.progress.cat_speed_limits'),
  overtaking: t('student.progress.cat_overtaking'),
  parking: t('student.progress.cat_parking'),
  intersections: t('student.progress.cat_intersections'),
  first_aid: t('student.progress.cat_first_aid'),
  safety: t('student.progress.cat_safety'),
  technical: t('student.progress.cat_technical')
}))

const formatCategoryName = (cat) => {
  return categoryNames.value[cat] || cat
}

const formatDate = (date) => {
  if (!date) return ''
  return new Date(date).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

const formatDateTime = (date) => {
  if (!date) return ''
  return new Date(date).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const formatDuration = (seconds) => {
  if (!seconds && seconds !== 0) return '-'
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}:${String(secs).padStart(2, '0')}`
}

const roundLabel = (round) => round > 0 ? `${t('adminUsers.rounds.round')} ${round}` : '-'

const getLocalizedText = (item, prefix) => {
  const currentLocale = String(locale.value || 'ru').toLowerCase()

  if (currentLocale === 'kz') {
    return item?.[`${prefix}Kz`] || item?.[`${prefix}Ru`] || item?.[`${prefix}En`] || ''
  }

  if (currentLocale === 'en') {
    return item?.[`${prefix}En`] || item?.[`${prefix}Ru`] || item?.[`${prefix}Kz`] || ''
  }

  return item?.[`${prefix}Ru`] || item?.[`${prefix}Kz`] || item?.[`${prefix}En`] || ''
}

const modeLabel = (mode) => {
  if (mode === 'EXAM_MODE') return t('tests.modes.exam')
  if (mode === 'TRAINING_MODE') return t('tests.modes.training')
  if (mode === 'ASSIGNED_TEST') return t('adminAnalytics.assignedTests')
  if (String(mode || '').includes('MISTAKE')) return t('tests.mistakeReview')
  return mode || '-'
}

const assignmentStatusLabel = (status) => {
  if (status === 'COMPLETED') return t('adminAnalytics.assignmentCompleted')
  if (status === 'IN_PROGRESS') return t('adminAnalytics.assignmentInProgress')
  if (status === 'CANCELLED') return t('adminAnalytics.assignmentCancelled')
  return t('adminAnalytics.assignmentActive')
}

const studentAverageScore = computed(() => {
  const sessions = studentDetails.value?.sessions || []
  if (!sessions.length) return 0

  return Math.round(
    sessions.reduce((sum, session) => sum + Number(session.score || 0), 0) / sessions.length
  )
})

const totalDetailedAnswers = computed(() => {
  const sessions = studentDetails.value?.sessions || []
  return sessions.reduce((sum, session) => sum + Number(session.answeredCount || 0), 0)
})

const activeAssignments = computed(() => {
  return (studentDetails.value?.assignments || []).filter(assignment => assignment.status !== 'CANCELLED')
})

const assignableAnswers = computed(() => {
  const answers = assignSession.value?.answers || []
  const seen = new Set()

  return answers.filter(answer => {
    if (!answer.testId || seen.has(answer.testId)) return false
    seen.add(answer.testId)
    return true
  })
})

const loadStudentDetails = async (studentId) => {
  const response = await api.get(`/admin/student-analytics/${studentId}/details`)
  studentDetails.value = response.data
}

const openStudentDetails = async (student) => {
  detailsDialog.value = true
  detailsLoading.value = true
  selectedStudentId.value = student.id
  studentDetails.value = null

  try {
    await loadStudentDetails(student.id)
  } catch (error) {
    errorMessage.value = error.response?.data?.error || t('adminAnalytics.loadError')
    showError.value = true
    detailsDialog.value = false
  } finally {
    detailsLoading.value = false
  }
}

const openAssignDialog = (session) => {
  assignSession.value = session
  assignSelectedTestIds.value = assignableAnswers.value.map(answer => answer.testId)
  assignTitle.value = `${t('adminAnalytics.assignmentDefaultTitle')} ${formatDateTime(session.createdAt)}`
  assignNotes.value = ''
  assignDialog.value = true
}

const selectAllAssignableQuestions = () => {
  assignSelectedTestIds.value = assignableAnswers.value.map(answer => answer.testId)
}

const clearAllAssignableQuestions = () => {
  assignSelectedTestIds.value = []
}

const submitAssignedTests = async () => {
  if (!selectedStudentId.value || !assignSelectedTestIds.value.length || !assignSession.value) {
    return
  }

  assignLoading.value = true
  try {
    await api.post(`/admin/student-analytics/${selectedStudentId.value}/assign-tests`, {
      testIds: assignSelectedTestIds.value,
      title: assignTitle.value,
      notes: assignNotes.value,
      sourceSessionId: assignSession.value.id
    })

    successMessage.value = t('adminAnalytics.assignmentCreated')
    showSuccess.value = true
    assignDialog.value = false
    await loadStudentDetails(selectedStudentId.value)
  } catch (error) {
    errorMessage.value = error.response?.data?.error || t('adminAnalytics.assignmentCreateError')
    showError.value = true
  } finally {
    assignLoading.value = false
  }
}

const cancelStudentAssignment = async (assignment) => {
  cancelAssignmentLoadingId.value = assignment.id
  try {
    await api.post(`/admin/student-analytics/assignments/${assignment.id}/cancel`)
    successMessage.value = t('adminAnalytics.assignmentCancelledSuccess')
    showSuccess.value = true
    await loadStudentDetails(selectedStudentId.value)
  } catch (error) {
    errorMessage.value = error.response?.data?.error || t('adminAnalytics.assignmentCancelError')
    showError.value = true
  } finally {
    cancelAssignmentLoadingId.value = null
  }
}

onMounted(() => {
  loadData()
})
</script>

<style scoped>
.text-gradient {
  background: linear-gradient(45deg, #FFC107, #FF9800);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}
</style>
